/* eslint-disable react-refresh/only-export-components --
   This module is the one shared home for the rent-increase screens: the create/edit form (the only
   component with real logic) plus the small display helpers the list, plan and detail pages all need
   (money formatting, the "Property · Unit" heading, the status badge, the needs-action rule and the
   preview-step lookup). Keeping them here means each is written once. */

/**
 * RentIncreaseForm — the shared create/edit form for a rent increase, with a live plan preview.
 *
 * Used by NewRentIncreasePage (create) and RentIncreaseDetailPage (edit a DRAFT). The preview calls
 * POST /leases/:leaseId/rent-increases/preview (debounced) so the landlord sees the notices the law
 * requires, when each is due, any jurisdiction warnings and who will be emailed BEFORE a draft exists
 * (BR-LL-37). Warnings are advisory and never disable submit.
 */

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  previewRentIncrease,
  ApiClientError,
  type InitiateRentIncreaseBody,
  type RentIncreasePreview,
  type RentIncreaseResponse,
  type RentIncreaseStepResponse,
  type RentIncreaseWarning,
} from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatCalendarDate } from '../../utils/date';
import { asCalendarDateString, calendarDaysUntil, formatUnitLabel, todayInTimezone } from '@resida/shared';

// ---------------------------------------------------------------------------
// Shared display helpers (also used by RentIncreasesPage / RentIncreaseDetailPage)
// ---------------------------------------------------------------------------

export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** "Maple Court · Unit 4B"; just the property name for a single-family home (AP-001: never the DEFAULT sentinel). */
export function formatRentIncreaseHeading(propertyName: string, unitNumber: string | null): string {
  const unitLabel = formatUnitLabel(unitNumber);
  return unitLabel ? `${propertyName} · ${unitLabel}` : propertyName;
}

const STATUS_BADGE_VARIANT = {
  DRAFT: 'secondary',
  ACTIVE: 'info',
  COMPLETED: 'success',
  CANCELLED: 'danger',
} as const satisfies Record<RentIncreaseResponse['status'], 'secondary' | 'info' | 'success' | 'danger'>;

export function RentIncreaseStatusBadge({
  status,
  needsAction = false,
  'data-testid': testId,
}: {
  status: RentIncreaseResponse['status'];
  /** An ACTIVE increase with a bounced or overdue step: the badge turns destructive so the row stands out (BR-LL-39). */
  needsAction?: boolean;
  'data-testid'?: string;
}) {
  const flagged = needsAction && status === 'ACTIVE';
  return (
    <Badge variant={flagged ? 'danger' : STATUS_BADGE_VARIANT[status]} className="shrink-0" data-testid={testId}>
      {flagged ? `${status} · Needs action` : status}
    </Badge>
  );
}

interface NeedsActionItem {
  stepId: string;
  stepNumber: number;
  stepLabel: string;
  /** BOUNCED: an emailed notice bounced and the step reopened (BR-LL-36). OVERDUE: the step is past due. */
  kind: 'BOUNCED' | 'OVERDUE';
  /** Tenants whose emailed notice bounced or failed (empty for OVERDUE). */
  tenantNames: string[];
  /** ISO timestamp of the most recent bounce (BOUNCED only). */
  bouncedAt: string | null;
  /**
   * True when the bounce reopened a step the system had marked served (automatic email only, BR-LL-36). A
   * manually sent email that bounces leaves its step pending: nothing was reopened.
   */
  reopened: boolean;
}

/**
 * BR-LL-36: only a step the system had marked served can be reopened, and only AUTOMATIC_EMAIL increases are
 * completed by the system. The API's `reopenedAt` is set for any open step carrying a bounce, so a manual-mode
 * step that merely has a bounced email must not be described as reopened.
 */
export function isStepReopened(
  rentIncrease: Pick<RentIncreaseResponse, 'serviceMode'>,
  step: Pick<RentIncreaseStepResponse, 'reopenedAt' | 'status'>
): boolean {
  return rentIncrease.serviceMode === 'AUTOMATIC_EMAIL' && step.reopenedAt !== null && step.status === 'PENDING';
}

/** Steps of an ACTIVE rent increase the landlord must act on: a bounce-reopened step, or an overdue one. */
export function getNeedsAction(rentIncrease: Pick<RentIncreaseResponse, 'status' | 'steps' | 'serviceMode'>): NeedsActionItem[] {
  if (rentIncrease.status !== 'ACTIVE') return [];
  const items: NeedsActionItem[] = [];
  rentIncrease.steps.forEach((step, index) => {
    if (step.status !== 'PENDING' && step.status !== 'OVERDUE') return;
    const failed = step.emails.filter((e) => e.status === 'BOUNCED' || e.status === 'FAILED');
    if (failed.length > 0) {
      const bounces = failed.map((e) => e.bouncedAt).filter((at): at is string => at !== null);
      items.push({
        stepId: step.id,
        stepNumber: index + 1,
        stepLabel: step.label,
        kind: 'BOUNCED',
        tenantNames: failed.map((e) => e.tenantName),
        bouncedAt: bounces.length > 0 ? bounces.reduce((a, b) => (a > b ? a : b)) : null,
        reopened: isStepReopened(rentIncrease, step),
      });
    } else if (step.status === 'OVERDUE') {
      items.push({
        stepId: step.id,
        stepNumber: index + 1,
        stepLabel: step.label,
        kind: 'OVERDUE',
        tenantNames: [],
        bouncedAt: null,
        reopened: false,
      });
    }
  });
  return items;
}

/** The preview step that corresponds to a stored step (matched on notice label + due date). */
export function findPreviewStep(
  preview: RentIncreasePreview,
  step: Pick<RentIncreaseStepResponse, 'label' | 'dueDate'>
): RentIncreasePreview['steps'][number] | undefined {
  return preview.steps.find((p) => p.label === step.label && p.dueDate === step.dueDate);
}

const SERVICE_METHOD_LABEL: Record<string, string> = {
  personal: 'in person',
  mail: 'by mail',
  posted: 'posted at the unit',
  electronic: 'by email',
};

/** "in person, by mail" for the raw service-method codes the plan stores. */
export function formatServiceMethods(methods: string[]): string {
  return methods.map((m) => SERVICE_METHOD_LABEL[m] ?? m.replace(/_/g, ' ')).join(', ');
}

const ISO_DATE_PATTERN = /\b\d{4}-\d{2}-\d{2}\b/g;

/** The first of the month on or after `base` (YYYY-MM-DD): rent increases take effect on a first of the month (BR-LL-17). */
function firstOfMonthOnOrAfter(base: string): string {
  const [year, month, day] = base.split('-').map(Number) as [number, number, number];
  if (day === 1) return base;
  const next = new Date(Date.UTC(year, month, 1));
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * The earliest effective date that would leave the full notice period for every notice already past due:
 * the first of the month on or after `today` plus the longest of those notice lengths. A notice's length is
 * the effective date minus its due date.
 */
function earliestCompliantEffectiveDate(
  lateSteps: Array<{ dueDate: string }>,
  effectiveDate: string,
  today: string
): string {
  const noticeDays = Math.max(...lateSteps.map((s) => calendarDaysUntil(effectiveDate, s.dueDate)));
  const [year, month, day] = today.split('-').map(Number) as [number, number, number];
  const earliest = new Date(Date.UTC(year, month - 1, day + noticeDays));
  const earliestKey = `${earliest.getUTCFullYear()}-${String(earliest.getUTCMonth() + 1).padStart(2, '0')}-${String(earliest.getUTCDate()).padStart(2, '0')}`;
  return firstOfMonthOnOrAfter(earliestKey);
}

/**
 * A plan warning in plain words. Dates the server embeds as YYYY-MM-DD are shown as "Apr 28, 2027", and a
 * missed-deadline warning names each notice whose due date has already passed and the effective date to
 * move to (`steps` and `today` come from the plan preview and the property's calendar day; `effectiveDate`
 * is the plan's current effective date).
 */
export function describeWarning(
  warning: RentIncreaseWarning,
  steps: RentIncreasePreview['steps'] | undefined,
  today: string,
  effectiveDate: string
): string {
  const text = warning.message.replace(ISO_DATE_PATTERN, (date) => formatCalendarDate(asCalendarDateString(date)));
  if (warning.type !== 'STEP_DEADLINE_INFEASIBLE' || !steps) return text;
  const late = steps.filter((s) => s.dueDate < today);
  if (late.length === 0) return text;
  const lateText = late.map((s) => `${s.label} (was due ${formatCalendarDate(asCalendarDateString(s.dueDate))})`).join('; ');
  const moveTo = formatCalendarDate(asCalendarDateString(earliestCompliantEffectiveDate(late, effectiveDate, today)));
  return `${text} Past due: ${lateText}. Move the effective date to ${moveTo} or later to allow the full period.`;
}

// ---------------------------------------------------------------------------
// The form
// ---------------------------------------------------------------------------

type ServiceMode = NonNullable<InitiateRentIncreaseBody['serviceMode']>;

export interface RentIncreaseFormContext {
  propertyName: string;
  unitNumber: string | null;
  tenantNames: string[];
}

interface RentIncreaseFormValues {
  newRentAmountCents: number;
  effectiveDate: string;
  autoUpdateLease: boolean;
  serviceMode: ServiceMode;
  emailConsentAttested: boolean;
}

interface RentIncreaseFormProps {
  mode: 'create' | 'edit';
  leaseId: string;
  currentRentCents: number | null;
  context: RentIncreaseFormContext | null;
  /** The unit, so a tenant without an email can be fixed on the unit's tenant list. */
  unitId?: string | null;
  /** The property's IANA timezone: "today" for the preview is the property's calendar day. */
  timezone: string;
  /** Edit mode prefill; create mode starts empty with auto-update on and Manual service. */
  initialValues?: RentIncreaseFormValues;
  /** Persist the values. Throw to keep the form open; the message is shown to the landlord. */
  onSubmit: (values: RentIncreaseFormValues) => Promise<void>;
  onCancel: () => void;
}

const PREVIEW_DEBOUNCE_MS = 400;

/** BR-LL-17 (client-side nicety only — the server is the authority): effective date must be the first of a month.
 * Checked on the raw YYYY-MM-DD string, never via `new Date(...)`, to avoid timezone-shift parsing bugs. */
function isFirstOfMonth(dateStr: string): boolean {
  return dateStr.split('-')[2] === '01';
}

function parseAmountToCents(input: string): number | null {
  if (input.trim() === '') return null;
  const cents = Math.round(parseFloat(input) * 100);
  return Number.isNaN(cents) || cents <= 0 ? null : cents;
}

function centsToAmountInput(cents: number): string {
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

const WARNING_LABEL: Record<RentIncreasePreview['warnings'][number]['type'], string> = {
  CAP_EXCEEDED: 'Above cap.',
  ELIGIBILITY_TOO_EARLY: 'Too soon.',
  STEP_DEADLINE_INFEASIBLE: 'Short notice.',
};

const EDIT_CONFLICT_MESSAGE = 'This draft can no longer be edited because a document has been generated.';

export function RentIncreaseForm({
  mode,
  leaseId,
  currentRentCents,
  context,
  unitId,
  timezone,
  initialValues,
  onSubmit,
  onCancel,
}: RentIncreaseFormProps) {
  const [amountInput, setAmountInput] = useState(initialValues ? centsToAmountInput(initialValues.newRentAmountCents) : '');
  const [effectiveDate, setEffectiveDate] = useState(initialValues?.effectiveDate ?? '');
  const [autoUpdateLease, setAutoUpdateLease] = useState(initialValues?.autoUpdateLease ?? true);
  const [serviceMode, setServiceMode] = useState<ServiceMode>(initialValues?.serviceMode ?? 'MANUAL');
  const [attested, setAttested] = useState(initialValues?.emailConsentAttested ?? false);

  const [dateError, setDateError] = useState<string | null>(null);
  const [attestationError, setAttestationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [preview, setPreview] = useState<RentIncreasePreview | null>(null);
  // The effective date the shown preview was computed for (the input may be ahead of it while a refresh is in flight).
  const [previewedEffectiveDate, setPreviewedEffectiveDate] = useState('');
  const [previewState, setPreviewState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  const ids = useId();
  const attestationRef = useRef<HTMLInputElement>(null);
  const today = todayInTimezone(timezone);
  const amountCents = parseAmountToCents(amountInput);
  const previewable = amountCents !== null && effectiveDate !== '' && isFirstOfMonth(effectiveDate);

  // Live, debounced preview (BR-LL-37). A stale response is aborted when the inputs change again.
  useEffect(() => {
    if (amountCents === null || !previewable) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setPreviewState('loading');
      previewRentIncrease(leaseId, { newRentAmountCents: amountCents, effectiveDate, serviceMode }, controller.signal)
        .then((result) => {
          setPreview(result);
          setPreviewedEffectiveDate(effectiveDate);
          setPreviewState('ready');
        })
        .catch(() => {
          if (!controller.signal.aborted) setPreviewState('error');
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [leaseId, previewable, amountCents, effectiveDate, serviceMode]);

  const electronicPermitted = preview?.electronicServicePermitted === true;
  const consentRequired = electronicPermitted && preview?.consentAttestationRequired === true;
  // Where email service is not permitted the choice is absent and the mode is Manual (BR-LL-32).
  const effectiveMode: ServiceMode = electronicPermitted ? serviceMode : 'MANUAL';
  const recipientsWithEmail = preview?.recipients.filter((r) => r.hasEmail) ?? [];
  const recipientsWithoutEmail = preview?.recipients.filter((r) => !r.hasEmail) ?? [];

  function handleEffectiveDateChange(value: string) {
    setEffectiveDate(value);
    setDateError(value && !isFirstOfMonth(value) ? 'Effective date must be the first day of a month.' : null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (amountCents === null) {
      setSubmitError('Enter a valid new rent amount.');
      return;
    }
    if (!effectiveDate) {
      setSubmitError('Choose an effective date.');
      return;
    }
    if (!isFirstOfMonth(effectiveDate)) {
      setDateError('Effective date must be the first day of a month.');
      return;
    }
    if (effectiveMode === 'AUTOMATIC_EMAIL' && consentRequired && !attested) {
      setSubmitError(null);
      setAttestationError('Confirm your tenants agreed in writing to receive notices by email to use Automatic email.');
      attestationRef.current?.focus();
      return;
    }

    setSubmitError(null);
    setAttestationError(null);
    setIsSubmitting(true);
    try {
      await onSubmit({
        newRentAmountCents: amountCents,
        effectiveDate,
        autoUpdateLease,
        serviceMode: effectiveMode,
        emailConsentAttested: consentRequired && attested,
      });
    } catch (err) {
      if (mode === 'edit' && err instanceof ApiClientError && err.status === 409) {
        setSubmitError(EDIT_CONFLICT_MESSAGE);
      } else {
        setSubmitError(err instanceof Error ? err.message : 'Failed to save rent increase');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const delta =
    amountCents !== null && currentRentCents !== null && currentRentCents > 0 ? amountCents - currentRentCents : null;
  const deltaText =
    delta === null || currentRentCents === null
      ? null
      : `${delta >= 0 ? '+' : '-'}${formatCurrency(Math.abs(delta))} (${delta >= 0 ? '+' : '-'}${(
          (Math.abs(delta) / currentRentCents) * 100
        ).toFixed(1)}%)`;

  const contextLine = [
    context ? formatRentIncreaseHeading(context.propertyName, context.unitNumber) : null,
    context && context.tenantNames.length > 0 ? context.tenantNames.join(', ') : null,
    preview ? `${preview.jurisdiction.name} rules apply` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  const inputClass =
    'w-full px-3 py-2 text-sm border border-input bg-background text-foreground rounded-md box-border min-h-[44px] focus:outline-none focus:ring-2 focus:ring-ring';
  const submitLabel = mode === 'create' ? 'Create draft' : 'Save changes';
  const submittingLabel = mode === 'create' ? 'Creating...' : 'Saving...';

  return (
    <div className="flex flex-col gap-4" data-testid="rent-increase-form-root">
      {contextLine && (
        <p className="m-0 text-sm text-muted-foreground" data-testid="rent-increase-form-context">
          {contextLine}
        </p>
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <form
          onSubmit={handleSubmit}
          className="flex min-w-0 flex-1 flex-col gap-5"
          data-testid="new-rent-increase-form"
          aria-label={mode === 'create' ? 'Plan a rent increase' : 'Edit rent increase'}
        >
          {submitError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert" data-testid="rent-increase-form-error">
              <p className="m-0 text-destructive">{submitError}</p>
            </div>
          )}

          {currentRentCents !== null && (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-muted-foreground">Current rent</span>
              <span className="text-lg text-foreground" data-testid="rent-increase-current-rent">
                {formatCurrency(currentRentCents)} / month
              </span>
            </div>
          )}

          <div>
            <label htmlFor={`${ids}-amount`} className="mb-1 block text-sm font-medium text-muted-foreground">
              New monthly rent *
            </label>
            <input
              id={`${ids}-amount`}
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              required
              aria-required="true"
              aria-describedby={deltaText ? `${ids}-delta` : undefined}
              placeholder="e.g. 2200"
              className={inputClass}
              data-testid="rent-increase-amount-input"
            />
            {deltaText && (
              <p id={`${ids}-delta`} className="mb-0 mt-1 text-xs text-muted-foreground" data-testid="rent-increase-amount-delta">
                {deltaText}
              </p>
            )}
          </div>

          <div>
            <label htmlFor={`${ids}-effective-date`} className="mb-1 block text-sm font-medium text-muted-foreground">
              Effective date *
            </label>
            <input
              id={`${ids}-effective-date`}
              type="date"
              value={effectiveDate}
              onChange={(e) => handleEffectiveDateChange(e.target.value)}
              required
              aria-required="true"
              aria-invalid={dateError ? 'true' : undefined}
              aria-describedby={`${ids}-effective-date-help${dateError ? ` ${ids}-effective-date-error` : ''}`}
              className={inputClass}
              data-testid="rent-increase-effective-date-input"
            />
            <p id={`${ids}-effective-date-help`} className="mb-0 mt-1 text-xs text-muted-foreground">
              Must be the first day of a month.
            </p>
            {dateError && (
              <p id={`${ids}-effective-date-error`} className="mb-0 mt-1 text-xs text-destructive" role="alert">
                {dateError}
              </p>
            )}
          </div>

          <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
            <input
              id={`${ids}-auto-update`}
              type="checkbox"
              checked={autoUpdateLease}
              onChange={(e) => setAutoUpdateLease(e.target.checked)}
              className="mt-1 h-[18px] w-[18px] shrink-0 rounded border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="rent-increase-auto-update-toggle"
            />
            <span className="text-sm text-foreground">
              Update the lease rent automatically when all steps are complete and the effective date arrives
            </span>
          </label>

          {electronicPermitted && preview && (
            <fieldset
              className="m-0 flex flex-col gap-3 rounded-lg border border-border p-4"
              data-testid="rent-increase-service-mode-group"
            >
              <legend className="px-1.5 text-sm font-semibold text-foreground">How will the notices be served?</legend>
              <label
                className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                  effectiveMode === 'AUTOMATIC_EMAIL' ? 'border-2 border-primary' : 'border-input'
                }`}
              >
                <input
                  type="radio"
                  name={`${ids}-service-mode`}
                  value="AUTOMATIC_EMAIL"
                  checked={effectiveMode === 'AUTOMATIC_EMAIL'}
                  onChange={() => setServiceMode('AUTOMATIC_EMAIL')}
                  className="mt-1 h-[18px] w-[18px] shrink-0 accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="rent-increase-service-mode-automatic"
                />
                <span>
                  <strong className="block text-[15px] text-foreground">Automatic email</strong>
                  <span className="mt-1 block text-[13px] text-muted-foreground">
                    {preview.jurisdiction.name} permits email service. We will email each notice to{' '}
                    <strong className="text-foreground">
                      {recipientsWithEmail.length > 0 ? recipientsWithEmail.map((r) => r.name).join(', ') : 'your tenants'}
                    </strong>{' '}
                    7 days before its deadline, or immediately on activation if that date has passed. Each step marks itself
                    done once the email is accepted. You do not need to click anything. If an email bounces, the step
                    reopens and we alert you right away.
                  </span>
                </span>
              </label>
              <label
                className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                  effectiveMode === 'MANUAL' ? 'border-2 border-primary' : 'border-input'
                }`}
              >
                <input
                  type="radio"
                  name={`${ids}-service-mode`}
                  value="MANUAL"
                  checked={effectiveMode === 'MANUAL'}
                  onChange={() => setServiceMode('MANUAL')}
                  className="mt-1 h-[18px] w-[18px] shrink-0 accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="rent-increase-service-mode-manual"
                />
                <span>
                  <strong className="block text-[15px] text-foreground">Manual</strong>
                  <span className="mt-1 block text-[13px] text-muted-foreground">
                    Nothing is sent for you. For each step you choose: download the notice and serve it yourself, or send
                    the email yourself. &ldquo;Mark served&rdquo; only records that you did it.
                  </span>
                </span>
              </label>

              {consentRequired && (
                <div className="flex flex-col gap-1">
                  <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
                    <input
                      ref={attestationRef}
                      id={`${ids}-attestation`}
                      type="checkbox"
                      checked={attested}
                      onChange={(e) => {
                        setAttested(e.target.checked);
                        setAttestationError(null);
                      }}
                      aria-required={effectiveMode === 'AUTOMATIC_EMAIL'}
                      aria-invalid={attestationError ? 'true' : undefined}
                      aria-describedby={`${ids}-attestation-help${attestationError ? ` ${ids}-attestation-error` : ''}`}
                      className="mt-1 h-[18px] w-[18px] shrink-0 rounded border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="rent-increase-consent-attestation"
                    />
                    <span className="text-sm text-foreground">
                      My tenants have agreed in writing to receive notices by email.
                    </span>
                  </label>
                  <p id={`${ids}-attestation-help`} className="m-0 text-xs text-muted-foreground">
                    {effectiveMode === 'AUTOMATIC_EMAIL'
                      ? 'Required for Automatic email.'
                      : 'Needed only if you will send notices by email yourself from the step page.'}
                  </p>
                  {attestationError && (
                    <p id={`${ids}-attestation-error`} className="m-0 text-xs text-destructive" role="alert" data-testid="rent-increase-consent-error">
                      {attestationError}
                    </p>
                  )}
                </div>
              )}

              {recipientsWithoutEmail.map((r) => (
                <div
                  key={r.tenantId}
                  role="status"
                  className="rounded-lg border border-warning/25 bg-warning/10 px-3 py-2.5 text-[13px] text-warning"
                  data-testid={`rent-increase-recipient-no-email-${r.tenantId}`}
                >
                  <p className="m-0">
                    {r.name} has no email address on file, so will not receive the email. Add one on the tenant record, or
                    serve their notice another way: that step will stay open with a prompt.
                  </p>
                  <Link
                    to={unitId ? `/units/${unitId}` : `/leases/${leaseId}`}
                    className="mt-1 flex min-h-[44px] items-center font-medium underline"
                    data-testid={`rent-increase-recipient-no-email-link-${r.tenantId}`}
                  >
                    Add an email on the tenant record
                  </Link>
                </div>
              ))}
            </fieldset>
          )}

          {preview && !electronicPermitted && (
            <p className="m-0 text-[13px] text-muted-foreground" data-testid="rent-increase-service-mode-fixed">
              Email service is not permitted for this notice in {preview.jurisdiction.name}. You serve each notice
              yourself, then mark the step served.
            </p>
          )}

          <div className="sticky bottom-0 z-10 -mx-[var(--page-padding-x)] flex flex-col gap-2 border-t border-border bg-background px-[var(--page-padding-x)] py-3 lg:static lg:mx-0 lg:border-0 lg:bg-transparent lg:p-0">
            {preview && preview.warnings.length > 0 && (
              <p className="m-0 text-xs font-medium text-warning lg:hidden" data-testid="rent-increase-warning-summary">
                {preview.warnings.length} warning{preview.warnings.length !== 1 ? 's' : ''} in the plan preview (advisory)
              </p>
            )}
            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={isSubmitting || !!dateError} data-testid="new-rent-increase-submit-button">
                {isSubmitting ? submittingLabel : submitLabel}
              </Button>
              <Button type="button" variant="outline" onClick={onCancel} data-testid="rent-increase-form-cancel-button">
                Cancel
              </Button>
            </div>
          </div>
          <p className="m-0 text-[13px] text-muted-foreground">
            A draft sends nothing. You can change any of this until you activate it.
          </p>
        </form>

        <aside
          className="flex min-w-0 flex-col gap-4 rounded-lg border border-border bg-card p-[var(--card-padding)] lg:w-[360px] lg:shrink-0"
          aria-label="Plan preview"
          aria-busy={previewState === 'loading'}
          data-testid="rent-increase-preview"
        >
          <h2 className="m-0 text-base font-semibold text-foreground">
            Plan preview{' '}
            <span className="text-[13px] font-normal text-muted-foreground">
              · {previewState === 'loading' ? 'updating...' : 'updates as you type'}
            </span>
          </h2>

          {!preview && previewState !== 'loading' && previewState !== 'error' && (
            <p className="m-0 text-sm text-muted-foreground" data-testid="rent-increase-preview-empty">
              Enter a new rent and an effective date to see the notices the law requires and when each is due.
            </p>
          )}
          {previewState === 'error' && (
            <p className="m-0 text-sm text-muted-foreground" role="status" data-testid="rent-increase-preview-error">
              The preview is unavailable right now. You can still create the draft and review the plan afterwards.
            </p>
          )}

          {preview && (
            <div className="flex flex-col gap-4" aria-live="polite">
              {preview.warnings.length > 0 && (
                <ul className="m-0 flex list-none flex-col gap-2 p-0" data-testid="rent-increase-preview-warnings">
                  {preview.warnings.map((w, i) => (
                    <li
                      key={`${w.type}-${i}`}
                      className="rounded-lg border border-warning/25 bg-warning/10 px-3 py-2 text-[13px] text-warning"
                      data-testid={`rent-increase-preview-warning-${w.type}`}
                    >
                      <strong>{WARNING_LABEL[w.type]}</strong> {describeWarning(w, preview.steps, today, previewedEffectiveDate)} Advisory only.
                    </li>
                  ))}
                </ul>
              )}

              <div>
                <h3 className="mb-1.5 mt-0 text-[13px] font-semibold text-foreground">Steps</h3>
                <ol className="m-0 flex list-none flex-col gap-2 p-0" data-testid="rent-increase-preview-steps">
                  {preview.steps.map((step, i) => (
                    <li
                      key={step.noticeId}
                      className="rounded-lg border border-border px-3 py-2.5"
                      data-testid={`rent-increase-preview-step-${i}`}
                    >
                      <div className="text-sm font-medium text-foreground">
                        {i + 1} · {step.label}
                      </div>
                      <div className="text-[13px] text-muted-foreground">
                        Due {formatCalendarDate(asCalendarDateString(step.dueDate))}
                        {step.scheduledEmailAt
                          ? ` · email scheduled ${formatCalendarDate(asCalendarDateString(step.scheduledEmailAt))}${
                              step.scheduledEmailAt <= today ? ' (sends at activation: inside the 7-day window)' : ''
                            }`
                          : ''}
                        {step.requiresExecution ? ' · requires your signature, always manual' : ''}
                      </div>
                    </li>
                  ))}
                </ol>
                {!previewable && (
                  <p className="mb-0 mt-2 text-xs text-muted-foreground">
                    Showing the last preview. Enter a valid amount and effective date to refresh it.
                  </p>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
